import defu from 'defu';
import type { FetchOptions, ResponseType } from 'ofetch';
import { parseCookies, VISITOR_COOKIE } from 'pb-tracking';
import { isDemoMode } from '~/services/demo/mode';
import { failureReasonOf } from 'pb-auth-contract';
import { readFetchFailure } from 'pb-api-errors';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';

/** Transport options for a call, minus the headers this layer owns and merges itself. */
export type PbApiOptions = Omit<FetchOptions<ResponseType>, 'headers' | 'method'> & {
    headers?: Record<string, string>;
    method?: HttpMethod;
};

// Bounds every request so a hung backend call fails visibly instead of holding a UI-level saving
// latch open forever. 30s comfortably covers a multi-list marketing opt-out PATCH fan-out
// (fifteen-ish lists, per the directory's current size) plus normal network latency; revisit
// once a bulk action is measured against production-shaped data.
export const API_REQUEST_TIMEOUT_MS = 30_000;

export async function usePbApi<T>(url: string, options: PbApiOptions = {}) {
    // Demo mode answers from fixtures before any auth or network work happens, so a
    // demo session needs no token and no request ever leaves the browser.
    //
    // Imported on demand, never at module scope: the fixtures reach the license types,
    // which reach the global store through an auto-import, which comes back here. A
    // static edge closes that loop and leaves whichever module lost the race holding an
    // uninitialised enum. Loading it here also keeps the fixture set out of the bundle
    // every real member downloads.
    if (isDemoMode()) {
        const { resolveDemoRequest } = await import('~/services/demo');
        return await resolveDemoRequest<T>(url, options);
    }

    const auth = useAuthStore();
    const config = useRuntimeConfig();

    const defaults = {
        baseURL: config.public.pb.apiDomain,
        headers: {
            Accept: 'application/json',
        },
    };

    const params: PbApiOptions & { headers: Record<string, string> } = defu(options, defaults);

    let status: number = 200;
    let message: string = '';
    let data: T | null = null;
    // The wait a refusal named, read off its `Retry-After`; a throttled route says it on its 429.
    let retryAfter: number | null = null;
    let retryAttempted = false;

    async function makeRequest(): Promise<void> {
        // Get a valid token (handles refresh and queueing automatically)
        const token = await auth.getValidTokenForRequest();

        // Set Authorization header if we have a token
        if (token) {
            params.headers.Authorization = token;
        }

        // Marketing attribution: forward the edge-minted first-party visitor id so
        // the API can attribute a conversion to the campaign that produced it. It is
        // read from the jar as the request leaves, the way the edge reads it: a
        // useCookie ref made here belongs to no scope, so nothing would ever dispose
        // of it, and every request would leave a cookie listener behind (in a browser
        // with no Cookie Store API, an open BroadcastChannel).
        const visitorId = parseCookies(document.cookie)[VISITOR_COOKIE];
        if (visitorId) {
            params.headers['x-pb-visitor-id'] = visitorId;
        }

        const result = await $fetch<T>(url, {
            ...params,
            retry: 0,
            // Named last so the shared bound is what a caller that says nothing gets. A caller
            // that does say something means it: a card charge cannot be given up on at 30s, and
            // a poll that asks an in-flight attempt how it ended must not hold the whole budget.
            timeout: params.timeout ?? API_REQUEST_TIMEOUT_MS,
            onResponse({ response }) {
                status = response.status;
            },
        });
        data = result as T;
    }

    try {
        await makeRequest();
    } catch (err: unknown) {
        // Status, body, sentence and any wait named come from the one reading every app gives a
        // failed request (`pb-api-errors`): a v3 envelope's own sentence, or the failure's text
        // for a log.
        const failure = readFetchFailure(err);
        status = failure.status ?? 500;
        data = (failure.body || null) as T;
        retryAfter = failure.retryAfter;

        // Only attempt refresh once per request to prevent infinite loops. A lapsed session's 401
        // names no reason (`{"message": "Unauthenticated."}`); one whose body names a reason under
        // `data` — any reason, one this build has never heard of included — is the API refusing
        // the request on its own terms, such as a wrong authenticator code. Renewing and replaying
        // that would present the same refused code twice against the member's five tries, and a
        // renewal the API declined would sign them out over a typo.
        if (status === 401 && !retryAttempted && failureReasonOf(data) === null) {
            retryAttempted = true;
            
            // Attempt to refresh token (this handles queuing internally to prevent race conditions)
            try {
                const refreshed = await auth.refreshAccessToken();
                if (refreshed) {
                    // Refresh succeeded, retry the original request
                    await makeRequest();
                } else {
                    // Refresh failed - auth store has already redirected to login
                    message = 'Session expired. Please log in again.';
                }
            } catch (retryErr: unknown) {
                // Refresh threw an error - just return the error, no redirect needed
                // (auth store already handles redirects). Status, body and message all come
                // from the one failure, so a replay the API refused for its own reason (a
                // wrong authenticator code once the session was renewed) still carries it.
                const retried = readFetchFailure(retryErr);
                status = retried.status ?? 500;
                data = (retried.body || null) as T;
                retryAfter = retried.retryAfter;
                message = retried.message;
            }
        } else {
            // Non-401 error or already retried - return error
            message = failure.message;
        }
    }

    return { data: data as T, status, message, retryAfter };
}
